import type { Catalog } from "./fr.ts";

/** English catalogue — keyed against the French source of truth. */
const en: Record<keyof Catalog, string> = {
  "brand.aria": "Mondial 2026 — home",
  "brand.hosts": "Host nations: United States · Canada · Mexico",
  "mode.aria": "Display mode",
  "mode.dashboard": "Dashboard",
  "mode.projection": "Projection",
  "mode.sim": "Simulation",
  "mode.stats": "Statistics",
  "mode.method": "Method",
  "mode.method.aria": "How the stats are computed",
  replay: "Replay",
  "replay.title": "Replay the results so far",
  "theme.aria": "Toggle light / dark mode",
  "theme.title": "Light / dark",
  "refresh.title": "Refresh now",
  "lang.aria": "Language",
  "rotate.hint": "Rotate your phone to portrait for a better experience",
  "rotate.dismiss": "Dismiss",
  "fresh.connecting": "connecting to data…",
  "fresh.unavailable": "data unavailable",
  "fresh.up": "up to date",
  "fresh.late": "delayed",
  "fresh.offline": "offline",
  "fresh.state": "Data: {state}",
  "banner.cached": "Data from {date} — refresh unavailable",
  "banner.degraded": "Live data unavailable — showing the base schedule",
  "foot.source": "Live source",
  "foot.dataFrom": "data from {date}",
  "foot.lastCalc": "Last computed",
  "foot.tz": "Times shown in your timezone",
  "foot.disclaimer":
    "Informational statistical model — not a guaranteed prediction. Flags: flag-icons (jsDelivr).",
  "ago.s": "{n} s ago",
  "ago.min": "{n} min ago",
  "ago.h": "{n} h ago",
  "cd.imminent": "imminent",
  "cd.waiting": "waiting for live",
  "cd.days": "in {d}d {h}h",
  "cd.hours": "in {h}h {m}min",
  "cd.mins": "in {m}min {s}s",
  "cd.secs": "in {s}s",
  today: "Today",
  "today.short": "Today",
  "today.scrollAria": "Matches (horizontal scroll)",
  "today.untilTomorrow": "until 9am tomorrow",
  "today.match.one": "{n} match",
  "today.match.other": "{n} matches",
  "today.noMatch": "no match",
  "today.none": "No match on that day.",
  "today.choose": "Choose a day",
  "lf.stats": "Statistics",
  "lf.lineups": "Line-ups",
  "lf.lineupsNone": "Line-ups not announced",
  "round.group": "Group stage",
  "round.r32": "Round of 32",
  "round.r16": "Round of 16",
  "round.qf": "Quarter-final",
  "round.sf": "Semi-final",
  "round.third": "Third-place play-off",
  "round.final": "Final",
  // --- i18n sweep 28/06 : chaînes auparavant codées en dur (composants/fiches) ---
  "bracket.final": "FINAL",
  "bracket.round.qfShort": "QF",
  "bracket.round.sfShort": "SF",
  "bracket.thirdPlace": "3RD PLACE",
  "bracket.title": "Knockout bracket",
  "clubs.col.assists": "Assists",
  "clubs.col.assists.title": "Sort by cumulative assists",
  "clubs.col.avgRating": "Avg rating",
  "clubs.col.avgRating.title": "Sort by average rating (click a 2nd column to break ties)",
  "clubs.col.club": "Club",
  "clubs.col.goals.title": "Sort by cumulative goals of the club's players",
  "clubs.col.players.title": "Sort by number of rated players",
  "clubs.note": "For each club, we take its players present at the World Cup: the RATING is the average of their tournament rating (each player counts once); GOALS and ASSISTS are their cumulative totals at the World Cup. Only clubs with at least 3 rated players are ranked. Click a header to sort — a 2nd column breaks ties (¹ primary, ² secondary). Hover a row for its top contributors.",
  "clubs.row.title": "{name} — breakdown by player · {tip}",
  "clubs.section.count": "average rating of their internationals at the World Cup",
  "clubs.section.title": "Clubs at the World Cup",
  "common.absent": "Out",
  "common.asOf": "as of",
  "common.assists": "assists",
  "common.assists.v2": "assists",
  "common.avgAbbr": "avg",
  "common.champion": "champion",
  "common.close": "Close",
  "common.draw": "draw",
  "common.dribbles": "dribbles",
  "common.duels": "duels",
  "common.eliminated": "eliminated",
  "common.finished": "FINISHED",
  "common.goals": "goals",
  "common.halfTime": "Half-time",
  "common.halfTimeAbbr": "HT",
  "common.keyPasses": "key passes",
  "common.live": "LIVE",
  "common.loading": "loading…",
  "common.loading.v2": "Loading…",
  "common.match": "match",
  "common.matchesAbbr": "m.",
  "common.ordinal.first": "st",
  "common.ordinal.other": "th",
  "common.ordinal.suffix": "st / th",
  "common.penaltiesAbbr": "PEN",
  "common.penaltiesAbbr.v2": "pens",
  "common.player": "player",
  "common.players": "players",
  "common.points": "points",
  "common.pos.attackers": "Forwards",
  "common.pos.defenders": "Defenders",
  "common.pos.midfielders": "Midfielders",
  "common.pos.others": "Other",
  "common.postponed": "postponed",
  "common.rated": "rated",
  "common.suspended": "Suspended",
  "common.tackles": "tackles",
  "common.top": "top",
  "common.upcoming": "Upcoming",
  "common.vs": "vs",
  "common.winAbbr": "W",
  "common.yearsOld": "yrs",
  "elo.adjusted.title": "adjusted rating (declared withdrawals)",
  "elo.avg.row": "Tournament average",
  "elo.col.sort.title": "Sort by {label} (click a 2nd column to break ties)",
  "elo.col.value": "Value",
  "elo.delta.title": "change since 06/11",
  "elo.note": "World Football Elo Ratings formula (K = 60) · {n} team already moved · Δ since pre-tournament · Rating = average team rating over the tournament · squad value (Transfermarkt) · click a header to sort — a 2nd column breaks ties (¹ primary, ² secondary); a Rating or a Value opens the player-by-player breakdown · * adjusted rating.",
  "elo.note.btn.title": "Breakdown by player — average competition rating (over {n} rated match)",
  "elo.section.count": "live · recalculated after each match ends",
  "elo.section.title": "Elo ranking",
  "elo.value.btn.title": "Player-by-player breakdown + source of the value",
  "elo.value.millions": "€{n}M",
  "fiche.closeDetail": "Close details",
  "fiche.closeProfile": "Close profile",
  "fiche.country.attack.fair": "in line with its xG",
  "fiche.country.attack.for": "for",
  "fiche.country.attack.over": "scores more than its xG",
  "fiche.country.attack.subtitle": "goals vs xG",
  "fiche.country.attack.title": "Attacking efficiency",
  "fiche.country.attack.under": "scores less than its xG",
  "fiche.country.conversionWord": "conversion",
  "fiche.country.defense.bad": "concedes more than opponents' xG",
  "fiche.country.defense.concededWord": "conceded",
  "fiche.country.defense.fair": "in line with opponents' xG",
  "fiche.country.defense.good": "concedes less than opponents' xG",
  "fiche.country.defense.oppXg": "opponents' xG",
  "fiche.country.defense.prevented": "prevented",
  "fiche.country.defense.subtitle": "conceded vs opponents' xG",
  "fiche.country.defense.title": "Defensive solidity",
  "fiche.country.dialogLabel": "Country profile",
  "fiche.country.lowSample": "few matches, to be confirmed",
  "fiche.country.over": "across",
  "fiche.country.rendement": "Performance vs value:",
  "fiche.country.rendement.value": "value",
  "fiche.country.results": "Results",
  "fiche.country.squad.title": "Squad",
  "fiche.country.squad.unavailable": "Squad unavailable (loaded from the archive in production).",
  "fiche.country.tile.rank": "rank",
  "fiche.country.tile.squadValue": "squad value",
  "fiche.country.value.fair": "at its market value",
  "fiche.country.value.over": "above its market value",
  "fiche.country.value.under": "below its market value",
  "fiche.country.xgDiff.conceded": "conceded",
  "fiche.country.xgDiff.created": "created",
  "fiche.country.xgDiff.title": "xG differential",
  "fiche.deserved.cell": "{xg} xG → {goals}",
  "fiche.deservedTitle": "Deserved result (xG)",
  "fiche.dominance": "Dominance",
  "fiche.eventsLive": "Live events",
  "fiche.eventsMatch": "Match events",
  "fiche.expectedGoalsModel": "Expected goals (model)",
  "fiche.h2h.draws": "{d} draw",
  "fiche.h2h.empty": "No recorded meetings",
  "fiche.h2h.meetings": "{n} meeting",
  "fiche.h2h.title": "Head-to-head",
  "fiche.h2h.winsTitle": "{w} win",
  "fiche.htScore": "HT {h}–{a}",
  "fiche.overlayAria": "Match details",
  "fiche.player.age": "{age} yrs",
  "fiche.player.apps": "apps",
  "fiche.player.avgRating": "avg rating",
  "fiche.player.dialogLabel": "Player profile",
  "fiche.player.fallbackName": "Player",
  "fiche.player.lastMatch": "Last match",
  "fiche.player.matchByMatch": "Match by match",
  "fiche.player.metrics.cumTournament": "tournament total",
  "fiche.player.metrics.profile": "{pos} profile",
  "fiche.player.metrics.title": "Metrics",
  "fiche.player.metrics.vsPosAvg": "vs position average",
  "fiche.player.noMatches": "No archived match for this player yet.",
  "fiche.player.noStats": "No stats this season",
  "fiche.player.pos.attacker": "Forward",
  "fiche.player.pos.defender": "Defender",
  "fiche.player.pos.goalkeeper": "Goalkeeper",
  "fiche.player.pos.midfielder": "Midfielder",
  "fiche.player.ratingCurve.ariaLabel": "Player rating trend",
  "fiche.player.ratingCurve.title": "Rating trend",
  "fiche.player.seasonAvg": "season avg",
  "fiche.player.unavailable": "Profile unavailable",
  "fiche.player.vsOpp": "vs {opp}",
  "fiche.player.vsPosAvg": "vs {pos} tournament average",
  "fiche.playerRatings.avgOn": "average over {n}",
  "fiche.playerRatings.avgOnRated": "average over {n} rated",
  "fiche.playerRatings.dialogLabel": "Team rating by player",
  "fiche.playerRatings.empty": "No archived player ratings for this team (ratings appear once matches are played).",
  "fiche.playerRatings.foot": "Average rating across World Cup matches (per-match ratings from API-Football) · sorted by rating descending · click a name for the match-by-match curve.",
  "fiche.playerRatings.subtitle": "Tournament ratings",
  "fiche.preMatch": "Pre-match",
  "fiche.probabilityDraw": "Probability — draw {draw}",
  "fiche.recentForm": "Recent form",
  "fiche.squadValue.empty": "Player breakdown unavailable for this team.",
  "fiche.squadValue.footClickSource": "Click {icon} for the source.",
  "fiche.squadValue.footPrefix": "Transfermarkt values",
  "fiche.squadValue.footSum": "sum of the {n} most valuable players (input to the \"Elo + value\" model).",
  "fiche.squadValue.sourceTitle": "Source: Transfermarkt",
  "fiche.squadValue.subtitle": "Squad value",
  "fiche.squadValue.viewOn": "View {name} on Transfermarkt",
  "fiche.standing": "Standings",
  "fiche.statsLive": "Live stats",
  "fiche.statsMatch": "Match stats",
  "fiche.teamRating.curve.ariaLabel": "Team rating trend match by match",
  "fiche.teamRating.curve.title": "Team rating trend",
  "fiche.teamRating.curve.trendTitle": "Trend over recent matches",
  "fiche.teamRating.curveTrend.down": "falling",
  "fiche.teamRating.curveTrend.flat": "steady",
  "fiche.teamRating.curveTrend.up": "rising",
  "fiche.teamRating.tagTitle": "Team rating — minutes-weighted average ({n} rated players)",
  "fiche.teamRating.trend.down": "form falling",
  "fiche.teamRating.trend.flat": "form steady",
  "fiche.teamRating.trend.up": "form rising",
  "fiche.teamRating.trendTitleSuffix": "(recent team rating)",
  "fiche.tournamentXi": "Team of the tournament",
  "groups.legend.direct": "qualified directly",
  "groups.legend.live": "live score included",
  "groups.legend.thirds": "third place in qualifying zone (rank among third-placed teams)",
  "groups.liveScoreTitle": "Live score included",
  "groups.playedCount": "{played}/6 MP",
  "groups.provisionalTitle": "Provisional standings (match in progress)",
  "groups.thirdRankBadge": "3rd&nbsp;#{n}",
  "groups.thirdRankTitle": "Rank among third-placed teams",
  "lb.apps": "{n} m",
  "lb.col.assists": "Assists",
  "lb.col.ratings": "Ratings",
  "lb.col.scorers": "Scorers",
  "lb.empty": "None yet",
  "lb.section.count": "since the start of the tournament",
  "lb.section.title": "Player rankings",
  "lb.unit.assist.one": "assist",
  "lb.unit.goal.one": "goal",
  "lf.assistBy": "assist {player}",
  "lf.awaitingLiveData": "awaiting live data",
  "lf.event.goal": "Goal",
  "lf.event.red": "Red card",
  "lf.event.subst": "Substitution",
  "lf.event.yellow": "Yellow card",
  "lf.expandMiniTitle": "{home} – {away} — show large (share the space)",
  "lf.expectedGoals": "Expected goals",
  "lf.groupMatchday": "Group {group} · Matchday {matchday}",
  "lf.groupMatchdayAbbr": "Group {group} · MD{matchday}",
  "lf.halfTimeScore": "Half-time {h}–{a}",
  "lf.inProgress": "in progress",
  "lf.kickoffAt": "Kickoff {time}",
  "lf.kickoffPassed": "KICKOFF PASSED",
  "lf.kickoffShort": "Kickoff",
  "lf.kickoffWaitingLive": "Kickoff {kick} · awaiting live data",
  "lf.kickoffYourLocalTime": "Kickoff {kick} · your local time",
  "lf.lineupsNotProvided": "Lineups not provided",
  "lf.lineupsTitle": "Lineups",
  "lf.lineupsUpper": "LINEUPS",
  "lf.minimizeMatch": "Minimize this match to a mini-card",
  "lf.modelPrediction": "model prediction",
  "lf.nextMatch": "NEXT MATCH",
  "lf.nextMiniAria": "Next match — hover or activate for the pre-match prediction",
  "lf.nextShort": "NEXT",
  "lf.opponentsUnknown": "Opponents not yet known — prediction coming.",
  "lf.penaltiesScore": "PSO {h}–{a}",
  "lf.predictionDraw": "Prediction — draw {draw}",
  "lf.predictionDrawLabel": "Prediction — draw {pct}",
  "lf.strengthElo": "Strength (Elo)",
  "lf.yourLocalTime": "your local time",
  "lineup.absentUncertain": "absent/doubtful",
  "lineup.captain": "captain",
  "lineup.startingLineupsAria": "Starting lineups",
  "lineup.substitutes": "Substitutes",
  "method.elo": "Real World Football Elo Ratings as of 11/06/2026 (eloratings.net), updated after each finished match (K = 60, host advantage +100, shootout counted as a draw). * Ratings adjusted for declared withdrawals: NED −22, BRA −10, CAN −12.",
  "method.footer": "<b>Caveats:</b> at the scale of a World Cup (1 to 7 matches), deviations from xG are mostly down to <b>chance</b> — these metrics <b>describe</b> what happened, they do not <b>predict</b>. The thresholds in parentheses (≥ 3 shots, ≥ 5 duels, xG ≥ 3…) are safeguards: below them, the stat is hidden or its verdict is not colored. xG comes from a single model (API-Football).",
  "method.goals": "Expected goals: Elo→Poisson mapping from the original model (v2 continuity), host advantage +90 on the prediction side. Ties in the knockout stage: extra time (λ×⅓) then penalty shootout based on goalkeeper quality (EA FC 26, {n}/48 teams covered, 50/50 otherwise — recent studies find no systematic advantage).",
  "method.intro": "Every number shown is either <b>fetched</b> from a data source or <b>computed</b> by the app from that data — never generated by an AI. Everything is <b>deterministic</b> and reproducible. Source legend:",
  "method.legend.apiFootball": "<b>API-Football</b> — match, team and player stats",
  "method.legend.appCalc": "<b>App computation</b> — derived from raw stats (formulas below)",
  "method.legend.otherFeeds": "<b>Other feeds</b> — Transfermarkt (value), eloratings (Elo), FIFA (official ranking)",
  "method.legend.simulation": "<b>Simulation</b> — Monte-Carlo Elo–Poisson (seeded, reproducible)",
  "method.row.bestThirds.d": "the 8 qualifiers: pts → diff → goals → FIFA ranking → draw. Real time (⏱)",
  "method.row.bestThirds.s": "Best third-placed teams",
  "method.row.bestThirds.w": "dedicated section",
  "method.row.bestXiClubs.d": "best XI by rating; average rating grouped by club",
  "method.row.bestXiClubs.s": "Team of the tournament · Ratings by club",
  "method.row.bestXiClubs.w": "\"Rankings\", clubs section",
  "method.row.citiesByPlace.d": "the group position sets the R32 slot → the whole path / venues follow from it",
  "method.row.citiesByPlace.s": "Cities by finishing position",
  "method.row.citiesByPlace.w": "Projection mode",
  "method.row.conversion.d": "goals ÷ shots (if ≥ 3 shots)",
  "method.row.conversion.w": "player panel \"Metrics\"",
  "method.row.cornersFoulsEtc.d": "raw match counts",
  "method.row.cornersFoulsEtc.s": "Corners · Fouls · Offsides · Cards · Saves",
  "method.row.defSolidity.d": "goals conceded ÷ opponent xG (same calculation, inverted semantics)",
  "method.row.deservedResult.d": "goals − xG: ≥ +0.8 \"over-performs\" · ≤ −0.8 \"wastes\" · otherwise \"true to play\"",
  "method.row.deservedResult.s": "Deserved result",
  "method.row.domination.d": "weighted index: xG·0.35 + possession·0.25 + shots·0.20 + on-target·0.10 + corners·0.10 → share per side (0–100)",
  "method.row.domination.w": "match panel",
  "method.row.elo.d": "strength of a team. Rn = Ro + K·G·(W − We), K = 60 in the final phase; recomputed after each finished match",
  "method.row.elo.s": "Elo (+ Δ)",
  "method.row.elo.w": "Elo ranking, country panel, live card",
  "method.row.fifaRank.d": "official world ranking (committed snapshot, frozen during the World Cup)",
  "method.row.fifaRank.s": "FIFA ranking",
  "method.row.fifaRank.w": "last tiebreaker criterion (internal)",
  "method.row.goalsAssists.d": "attacking output",
  "method.row.goalsAssists.s": "Goals · assists",
  "method.row.groupStandings.d": "FIFA Art. 13 tiebreak: head-to-head (pts/diff/goals) → overall diff → goals → fair-play → FIFA ranking → seeded draw. ⏱ if a live score is included",
  "method.row.groupStandings.s": "Group standings",
  "method.row.groupStandings.w": "groups grid",
  "method.row.h2h.d": "wins-draws-losses record + list of past meetings",
  "method.row.h2h.s": "Head-to-head (H2H)",
  "method.row.idem.w": "same",
  "method.row.injuries.d": "unavailabilities (refreshed 1×/day)",
  "method.row.injuries.s": "Injuries / absences",
  "method.row.injuries.w": "mini-pitch badge, model input",
  "method.row.lineups.d": "starting eleven + formation (e.g. 4-3-3) + coach",
  "method.row.lineups.w": "match panel → mini-pitch",
  "method.row.matchEvents.d": "goals, cards and substitutions with timestamps (minute + player)",
  "method.row.matchEvents.w": "match panel \"Events\" + live feed",
  "method.row.offEfficiency.d": "goals ÷ cumulative xG. Verdict (over/under-performs) colored only if cumulative xG ≥ 3 (otherwise: variance)",
  "method.row.passAcc.d": "% of passes completed over the match",
  "method.row.passAcc.s": "Completed passes",
  "method.row.per90.d": "total ÷ minutes × 90",
  "method.row.per90.s": "G+A / 90 · Key passes / 90 · Tackles / 90",
  "method.row.possession.d": "% of time with the ball",
  "method.row.possession.s": "Possession",
  "method.row.possession.w": "live card + match panel",
  "method.row.possibleOpponents.d": "distribution of who fills the opposing slot in the simulation",
  "method.row.possibleOpponents.s": "Possible opponents per round",
  "method.row.possibleOpponents.w": "Projection mode (tree)",
  "method.row.prediction.d": "1-X-2 probability (Elo → Poisson) + Elo + model xG",
  "method.row.prediction.s": "Pre-match prediction",
  "method.row.prediction.w": "\"next match\" card (hover), match panel",
  "method.row.probabilities.d": "frequency over a seeded, conditionable Monte-Carlo simulation",
  "method.row.probabilities.s": "Probabilities (title / final / qualification…)",
  "method.row.probabilities.w": "\"Probabilities\", Projection mode",
  "method.row.qualifConditions.d": "enumeration of all W/D/L results of the remaining matches + tiebreak rule (head-to-head, then goal difference)",
  "method.row.qualifConditions.s": "Qualification conditions",
  "method.row.qualifConditions.w": "Projection modal",
  "method.row.ratesPct.d": "average / success rate",
  "method.row.ratesPct.s": "% passes · Duels won % (≥5) · Dribbles % (≥3)",
  "method.row.rating.d": "provider match rating (0–10)",
  "method.row.rating.w": "player panel \"Match by match\", hover on the mini-pitch",
  "method.row.scoreMinuteStatus.d": "live or final result, minute played (+ live stoppage time, e.g. 90+4), status (upcoming / live / half-time / finished)",
  "method.row.scoreMinuteStatus.s": "Score · minute · status",
  "method.row.scoreMinuteStatus.w": "Today strip, Live card, match panel",
  "method.row.scorersAssists.d": "aggregated tops from the archive",
  "method.row.scorersAssists.s": "Scorers · Assisters · Ratings",
  "method.row.scorersAssists.w": "\"Rankings\"",
  "method.row.shots.d": "total shots, shots on target, blocked shots",
  "method.row.shots.s": "Shots · on target · blocked",
  "method.row.shotsPassesPct.d": "volume and accuracy",
  "method.row.shotsPassesPct.s": "Shots / on target · passes / key · % passes",
  "method.row.squadValue.d": "sum of the market value of the top-23 (refreshed 1×/day)",
  "method.row.squadValue.w": "country panel, Elo ranking, value modal (per-player detail)",
  "method.row.tacklesDuels.d": "defensive activity and ball-carrying",
  "method.row.tacklesDuels.s": "Tackles · duels (won/total) · dribbles (successful/attempted)",
  "method.row.teamEfficiency.d": "attacking over-performance (goals − xG) / defensive (conceded − xGA). Teams with xG ≥ 3 only; descriptive, not a talent hierarchy",
  "method.row.teamRating.d": "Σ(rating · minutes) ÷ Σ(minutes); trend = slope of the last ~4 ratings (threshold ±0.15)",
  "method.row.teamRating.s": "Team rating + trend ↗/↘",
  "method.row.teamRating.w": "live card badge, country panel curve",
  "method.row.vsPositionAvg.d": "(player value ÷ tournament average at his position − 1) × 100. Tackles/90 = a volume → shown but not colored good/bad",
  "method.row.vsPositionAvg.w": "same (grid ordered by position)",
  "method.row.xg.d": "sum of the goal probabilities of chances (provider model)",
  "method.row.xg.s": "xG (expected goals)",
  "method.row.xgDiff.d": "Σ(xG created − xG conceded) — the most robust measure on a small sample",
  "method.row.yield.d": "Elo rank vs value rank; gap ≥ 4 places → plays \"above / below its value\"",
  "method.row.yield.s": "Yield",
  "method.sec.calcMatch.title": "Computed metrics — match",
  "method.sec.calcPlayer.note": "Shown only above a minimum volume (given in parentheses); below that, hidden.",
  "method.sec.calcPlayer.title": "Computed metrics — player (tournament total)",
  "method.sec.calcTeam.title": "Computed metrics — team (tournament total)",
  "method.sec.context.title": "Team & player context",
  "method.sec.modelProjection.title": "Model & projection",
  "method.sec.playerMatch.title": "Per-match player stats",
  "method.sec.rawMatch.title": "Raw match data",
  "method.sec.standings.title": "Tournament standings",
  "method.sec.teamMatch.note": "Shown as home / away bars; on hover, an over-time evolution graph.",
  "method.sec.teamMatch.title": "Per-match team stats",
  "method.standings": "Standings: FIFA 2026 rules (head-to-head first); fair play not modelable from scores; last criterion: Elo ranking then seeded random draw 🎲. Monte Carlo simulation in a Web Worker, seeded and reproducible.",
  "method.summary": "Methodology",
  "method.table.head.definition": "Definition",
  "method.table.head.source": "Source",
  "method.table.head.stat": "Stat",
  "method.table.head.where": "Where",
  "method.title": "Method — how the stats are computed",
  "probs.metaBrowser": "browser · {ms} ms",
  "probs.metaDraws": "{n} draws",
  "probs.metaSeed": "seed {seed}",
  "probs.metaServer": "server (pushed)",
  "probs.modelTag": "Elo–Poisson model · Monte Carlo",
  "probs.preBelowThreshold": "< 0.3% pre-tournament",
  "probs.preLabelBelow": "< 0.3%",
  "probs.preTournamentTitle": "Pre-tournament: {pct}%",
  "probs.rowTitle": "{team} — pre-tournament {preLabel}",
  "probs.running": "Simulation running…",
  "probs.title": "Title probabilities",
  "probs.waiting": "Waiting for the first simulation…",
  "proj.champion": "Champion {pct}",
  "proj.computingMc": "Running Monte Carlo…",
  "proj.cond.bestThird": " (best third-placed)",
  "proj.cond.bothQualify": "if {team} and {opp} both reach this round",
  "proj.cond.dialogAria": "Match-up conditions",
  "proj.cond.if": "if {cond1} and {cond2}",
  "proj.cond.introDeep": "The path and possible scores for {team} and {opp} to meet in the {round}.",
  "proj.cond.introR32": "All combinations of remaining results that produce this Round-of-32 match.",
  "proj.cond.koNote": "Then {team} and {opp} each win their preceding knockout rounds ({rounds}) to meet in the {round}.",
  "proj.cond.ofGroup": "of Group {group}",
  "proj.cond.tieNote": "At equal points, the 2026 World Cup tiebreaker looks first at the head-to-head between the tied teams, then overall goal difference, then goals scored — which is why one match's score can swing another team's ranking.",
  "proj.cond.title": "Conditions — {team} vs {opp} · {round}",
  "proj.cta.continue": "Continue",
  "proj.cta.editScores": "Edit my scores",
  "proj.cta.nextRound": "Go to next round",
  "proj.cta.project": "Project with my scores",
  "proj.lessThan1pct": "< 1%",
  "proj.line.conditionsTitle": "Conditions to face {opp}",
  "proj.match.awayScoreAria": "Away score",
  "proj.match.homeScoreAria": "Home score",
  "proj.match.liveTitle": "live",
  "proj.match.playedTitle": "match played",
  "proj.modal.finish": "Finish",
  "proj.modal.finishRound": "Finish this round",
  "proj.modal.hint": "Enter the scores you imagine: your team's path and percentages recalculate instantly.",
  "proj.modal.reset": "Reset",
  "proj.modal.resetN": "Reset ({n})",
  "proj.modal.stepCount": "Round {i} / {n}",
  "proj.modal.tiebreakBody": " (World Cup 2026): 1) head-to-head — points, goal difference, goals scored; 2) overall goal difference; 3) goals scored; 4) fair play (cards); 5) FIFA ranking.",
  "proj.modal.tiebreakTitle": "Tiebreaker at equal points",
  "proj.opening": "Opening projection…",
  "proj.outcome.awayWins": "{away} beat {home}",
  "proj.outcome.draw": "{home}–{away}: draw",
  "proj.outcome.homeWins": "{home} beat {away}",
  "proj.path.heading": "Likely path",
  "proj.path.title": "Title",
  "proj.path.undetermined": "path not yet determined",
  "proj.pending.decidedBy": "decided by: {parts}",
  "proj.pending.group": "gr. {group}: {matches}",
  "proj.picker.label": "Project",
  "proj.picker.model": "Elo + value model",
  "proj.picker.recalc": "· recalculating…",
  "proj.place.1": "1st",
  "proj.place.2": "2nd",
  "proj.place.3": "3rd",
  "proj.pos.bestThird": "best third-placed",
  "proj.pos.heading": "Your cities change with your finishing position in Group {group}",
  "proj.pos.variableSlot": "· slot varies",
  "proj.preparing": "Preparing…",
  "proj.qualFallback.head": "{team} qualified from Group {group}",
  "proj.r32.whichResults": "Which results make this Round-of-32 match possible?",
  "proj.round.final": "Final",
  "proj.round.qf": "Quarter-finals",
  "proj.round.r16": "Round of 16",
  "proj.round.r32": "Round of 32",
  "proj.round.sf": "Semi-finals",
  "proj.short.qf": "QF",
  "proj.short.r16": "R16",
  "proj.short.r32": "R32",
  "proj.short.sf": "SF",
  "proj.solve.gdDeficit": "{rival} has a {buts} goal-difference lead → {me} must close the gap by winning more decisively",
  "proj.solve.gdEqual": "{me} and {rival} have the same goal difference → whoever wins more decisively finishes ahead",
  "proj.solve.gdLead": "{me} already has a {buts} goal-difference lead over {rival} → stays ahead by keeping it",
  "proj.solve.goalOther": "{n} goals",
  "proj.solve.h2hDraw": "head-to-head drawn → move to goal difference: {lead}",
  "proj.solve.h2hLoss": "{rival} won the head-to-head → {me} would need to gain the edge another way",
  "proj.solve.h2hWin": "{me} won the head-to-head → finishes ahead of {rival} (1st tiebreaker, settled)",
  "proj.solve.head": "{team} {place} of Group {group}",
  "proj.solve.more": "+ {n} more",
  "proj.solve.none": "No combination allows it.",
  "proj.solve.settledNo": "✗ no longer possible (group finished)",
  "proj.solve.settledOk": "✓ already secured (group finished)",
  "proj.solve.tiedWith": "→ level on points with {rival}, {txt}",
  "proj.solve.tooMany": "Too many matches still to play to list the combinations.",
  "proj.standings.gdTitle": "goal difference",
  "proj.standings.gfTitle": "goals for",
  "proj.standings.playedTitle": "played",
  "proj.toggle.absences": "absences",
  "proj.toggle.absTitle": "Subtracts Elo for missing players (injured/suspended). Experimental, capped at ±{cap}.",
  "proj.toggle.exp": "exp.",
  "proj.toggle.form": "form",
  "proj.toggle.formTitle": "Adjusts Elo by recent form (team rating). Experimental, capped at ±{cap}.",
  "proj.tree.heading": "Likely path bracket",
  "proj.tree.subhead": "Round of 32 as tabs · later rounds follow the chosen opponent",
  "proj.tree.undetermined": "Path not yet determined.",
  "proj.wizard.opponentIn": "Opponent in {round}",
  "proj.wizard.yourPlacement": "Your team's placement",
  "replay.groupPhaseDone": "Group stage finished",
  "replay.groupPhaseDoneToBracket": "Group stage finished — to the bracket",
  "replay.pauseAria": "Pause",
  "replay.pauseResumeTitle": "Pause / resume",
  "replay.resumeAria": "Resume",
  "replay.resuming": "Replaying results…",
  "replay.skipToLive": "Go to live",
  "replay.speedTitle": "Speed up",
  "replay.toBracket": "Go to the bracket",
  "sim.championTitle": "World champion 2026 (simulation)",
  "sim.footer.champion": "Champion: {code}. Flags: flag-icons (jsDelivr).",
  "sim.footer.deterministic": "most likely scenario, deterministic",
  "sim.footer.model": "Elo–Poisson model, {ratings}. This is not a guaranteed prediction.",
  "sim.footer.oneOfMany": "One realization among millions — {tail}",
  "sim.footer.relaunch": "relaunch for another draw",
  "sim.intro": "The model runs a full tournament — like a real draw. Pick the model, launch it, and watch the champion emerge.",
  "sim.label.fixedCount": "({n} fixed)",
  "sim.label.ratings": "Ratings: {value}",
  "sim.label.scenario": "Scenario: {value}",
  "sim.label.shootout": "Shootout: {value}",
  "sim.label.startFrom": "Start: {value}",
  "sim.newDraw": "New draw",
  "sim.ratings.legacy": "Legacy Elo",
  "sim.ratings.real": "Real Elo",
  "sim.ratings.value": "Elo + value",
  "sim.replay": "Replay",
  "sim.replayScenario": "Replay this scenario",
  "sim.runnerUp": "Runner-up",
  "sim.scenario.modal": "Most likely",
  "sim.scenario.random": "Random",
  "sim.scenarioFr.modal": "most likely",
  "sim.scenarioFr.random": "random",
  "sim.seg.scenario": "Scenario",
  "sim.seg.startFrom": "Start",
  "sim.shootout.gk": "Goalkeepers",
  "sim.startFr.fromReal": "from real results",
  "sim.startFr.scratch": "full tournament",
  "sim.startFrom.fromReal": "From real results",
  "sim.startFrom.scratch": "Full tournament",
  "sim.tabFr.gk": "goalkeepers",
  "sim.thirdPlace": "3rd place",
  "sim.title": "Tournament simulation",
  "stats.col.apps.title": "matches played (at least one minute)",
  "stats.col.assists.abbr": "A",
  "stats.col.assists.title": "assists (the last pass before a goal)",
  "stats.col.cards": "Cards",
  "stats.col.cards.group.title": "total yellow / red cards in the group",
  "stats.col.cards.match.title": "total cards in the match (yellow + red)",
  "stats.col.cards.team.title": "total yellow / red cards received",
  "stats.col.corners.abbr": "Cor",
  "stats.col.corners.group.title": "total corners won in the group",
  "stats.col.corners.team.title": "total corners won",
  "stats.col.date": "Date",
  "stats.col.date.title": "kickoff date and time (your time zone)",
  "stats.col.diff.abbr": "Diff",
  "stats.col.diff.title": "goal difference (scored − conceded)",
  "stats.col.duelWinPct.abbr": "Duels%",
  "stats.col.duelWinPct.title": "percentage of duels won (ground and aerial)",
  "stats.col.elo": "Elo",
  "stats.col.elo.title": "team strength (World Football Elo), refreshed after every match",
  "stats.col.goals": "Goals",
  "stats.col.goals.abbr": "G",
  "stats.col.goals.group.title": "total goals scored in the group",
  "stats.col.goals.match.title": "total goals in the match (both teams)",
  "stats.col.goals.player.title": "goals scored",
  "stats.col.goalsAgainst.abbr": "GA",
  "stats.col.goalsAgainst.title": "goals against: goals conceded",
  "stats.col.goalsFor.abbr": "GF",
  "stats.col.goalsFor.title": "goals for: goals scored",
  "stats.col.gpm.abbr": "G/m",
  "stats.col.gpm.title": "goals per match (average) in the group",
  "stats.col.group": "Group",
  "stats.col.group.title": "group stage group (✓ = its 6 matches are finished). Click = filter teams",
  "stats.col.keyPasses.abbr": "KP",
  "stats.col.keyPasses.title": "key passes: passes leading directly to a shot",
  "stats.col.match": "Match",
  "stats.col.match.title": "click = match panel (line-ups, events, stats)",
  "stats.col.minutes.abbr": "Min",
  "stats.col.minutes.player.title": "minutes played in the tournament (cumulative)",
  "stats.col.minutes.team.title": "minutes played — 90/match (120 with extra time) + stoppage time: status.extra live, otherwise estimated by summing each period's added time from events (the source does not give the total)",
  "stats.col.passPct.abbr": "Pass%",
  "stats.col.passPct.match.title": "pass-completion % of each team, in match order (left team – right team)",
  "stats.col.passPct.player.title": "pass-completion % followed by the total NUMBER of passes attempted it is based on — e.g. 90%/230 (a % over few passes is not very meaningful)",
  "stats.col.passPct.team.title": "team pass-completion % (aggregated over all its players) followed by the total NUMBER of passes attempted it is based on — e.g. 86%/4200; populated in production",
  "stats.col.played.abbr": "P",
  "stats.col.played.group.title": "matches played out of the group's 6",
  "stats.col.played.team.title": "matches played",
  "stats.col.player.title": "click = player panel (tournament stats, ratings curve)",
  "stats.col.points.abbr": "Pts",
  "stats.col.points.title": "group-stage points (win 3, draw 1, loss 0)",
  "stats.col.pos": "Position",
  "stats.col.pos.title": "position: Goalkeeper / Def(ender) / Mid(fielder) / Att(acker)",
  "stats.col.poss.abbr": "Poss",
  "stats.col.poss.match.title": "ball possession (%) of each team, in match order",
  "stats.col.possession.abbr": "Poss%",
  "stats.col.possession.title": "average ball possession (% of playing time)",
  "stats.col.rating": "Rating",
  "stats.col.rating.title": "player's average rating over the tournament (0–10, provider)",
  "stats.col.shots": "Shots",
  "stats.col.shots.group.title": "total shots attempted in the group",
  "stats.col.shots.match.title": "shots on target / total shots of each team, in match order (e.g. 5/12–2/8)",
  "stats.col.shots.player.title": "shots on target / total shots attempted in the tournament (e.g. 5/12)",
  "stats.col.shots.team.title": "shots on target / total shots attempted (e.g. 42/118)",
  "stats.col.stage": "Round",
  "stats.col.stage.title": "round of the competition (group stage or knockout)",
  "stats.col.team": "Team",
  "stats.col.team.title": "click = country panel (squad, results, Elo, value)",
  "stats.col.xg.group.title": "cumulative expected goals: total quality of chances created (provider model)",
  "stats.col.xg.match.title": "expected goals (xG) of each team, in match order — who deserved to score versus the scoreline",
  "stats.col.xg.team.title": "expected goals created: cumulative quality of the team's chances (provider model)",
  "stats.col.xga.title": "expected goals conceded: quality of chances allowed to the opponent",
  "stats.col.xgaHead": "xGA",
  "stats.col.xgHead": "xG",
  "stats.empty.waiting": "Waiting for data…",
  "stats.filter.allGroups": "All groups",
  "stats.filter.allStages": "All rounds",
  "stats.filter.byGroup.aria": "Filter by group",
  "stats.filter.byStage.aria": "Filter by round",
  "stats.filter.group.option": "Group {g}",
  "stats.filter.groupStage": "Group stage",
  "stats.footer.method.link": "Method",
  "stats.footer.note": "Groups, teams and matches: aggregated live from the feed (current score included, ⏱). Players: server aggregate, refreshed periodically. Definitions: Method tab.",
  "stats.footerNote": "Groups, teams and matches: aggregated live from the feed (current score included, ⏱). Players: server aggregate, refreshed periodically. Definitions: ",
  "stats.graph.ariaLabel": "Evolution of {label} over the course of the match",
  "stats.graph.buildingSub": "one reading every ~30 s — it appears in a moment",
  "stats.graph.buildingTitle": "Curve being built",
  "stats.graph.unavailableSub": "match already finished when tracking started — no retroactive history",
  "stats.graph.unavailableTitle": "Evolution unavailable",
  "stats.group.cell": "Group {group}",
  "stats.group.done.title": "group finished",
  "stats.group.row.title": "Filter the teams of group {group}",
  "stats.live.title": "match in progress",
  "stats.liveNow": "{n} live ⏱",
  "stats.metric.conversion": "Conversion",
  "stats.metric.dribblePct": "Successful dribbles",
  "stats.metric.duelWinPct": "Duels won",
  "stats.metric.gaPer90": "G+A / 90",
  "stats.metric.keyPassesPer90": "Key passes / 90",
  "stats.metric.passAcc": "Pass %",
  "stats.metric.tacklesPer90": "Tackles / 90",
  "stats.pill.corners": "corners",
  "stats.pill.goalsPerMatch": "goals / match",
  "stats.pill.redCards": "red cards",
  "stats.pill.shots": "shots",
  "stats.pill.xgTotal": "total xG",
  "stats.pill.yellowCards": "yellow cards",
  "stats.players.empty": "Player rankings unavailable here (populated by the archive in production, at the next aggregate).",
  "stats.search.placeholder": "Search for a player…",
  "stats.section.groups": "Groups",
  "stats.section.groups.hint": "click a group → filter the teams",
  "stats.section.matches": "Matches",
  "stats.section.players": "Players",
  "stats.section.teams": "Teams",
  "stats.shotsOnAbbr": "on tgt",
  "stats.stage.group": "Group · MD{md}",
  "stats.statMeta.blocked": "Blocked shots",
  "stats.statMeta.corners": "Corners",
  "stats.statMeta.fouls": "Fouls",
  "stats.statMeta.offsides": "Offsides",
  "stats.statMeta.red": "Red cards",
  "stats.statMeta.saves": "Saves",
  "stats.statMeta.shotsOnTarget": "Shots on target",
  "stats.statMeta.xg": "Expected goals (xG)",
  "stats.statMeta.yellow": "Yellow cards",
  "stats.title": "World Cup statistics",
  "teff.caveat": "Note: the gap to xG stays sensitive to chance over few matches — not a talent hierarchy.",
  "teff.col.attack": "Offensive over-performance",
  "teff.col.defense": "Defensive over-performance",
  "teff.main.conceded": "{n} conc.",
  "teff.main.goals": "{n} goal",
  "teff.section.count": "goals vs xG over the tournament",
  "teff.section.title": "Team efficiency",
  "thirds.col.goalDiff": "±",
  "thirds.col.group": "Grp",
  "thirds.criteria": "Tiebreakers: points, goal difference, goals scored, FIFA ranking, draw 🎲.",
  "thirds.liveNote": "live score included (provisional).",
  "thirds.subtitle": "advancing to the round of 32",
  "thirds.title": "The 8 best third-placed teams",
  "stats.ev.inMatch": "Minute-by-minute evolution",
  "stats.ev.perMatch": "Match by match in the tournament",
  "stats.ev.perNation": "Split by nation and matchday",
  "stats.ev.noData": "No data for this stat.",
  "stats.ev.cellHint": "See the evolution",
};

export default en;
